import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { adminApi } from "../auth";
import { PoundSterling, ShoppingBag, AlertTriangle, TrendingUp } from "lucide-react";

const Card = ({ title, value, Icon, testid }) => (
  <div className="vd-card p-5 flex items-center gap-4" data-testid={testid}>
    <span className="inline-flex items-center justify-center rounded-md" style={{ width: 48, height: 48, background: "rgba(230,0,0,0.10)" }}>
      <Icon size={22} color="var(--primary-red)" />
    </span>
    <div>
      <p className="vd-muted text-xs uppercase tracking-wider">{title}</p>
      <p className="vd-heading font-extrabold" style={{ fontSize: 24 }}>{value}</p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="vd-muted" data-testid="dashboard-loading">Loading…</p>;

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="mb-2" style={{ fontSize: 28 }}>Dashboard</h1>
      <p className="vd-muted mb-6">Live overview of revenue, orders and inventory.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stat-cards">
        <Card title="Total Revenue" value={`£${d.total_revenue.toFixed(2)}`} Icon={PoundSterling} testid="stat-total-revenue" />
        <Card title="Total Orders" value={d.total_orders} Icon={ShoppingBag} testid="stat-total-orders" />
        <Card title="Avg Order Value" value={`£${d.average_order_value.toFixed(2)}`} Icon={TrendingUp} testid="stat-aov" />
        <Card title="Low-Stock Alerts" value={d.low_stock.length} Icon={AlertTriangle} testid="stat-low-stock" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="vd-card p-5 lg:col-span-2" data-testid="dashboard-revenue-chart">
          <p className="vd-heading font-semibold mb-3">Revenue — last 7 days</p>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={d.seven_day_revenue}>
                <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                <YAxis tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", color: "var(--text-heading)" }} />
                <Line type="monotone" dataKey="revenue" stroke="var(--primary-red)" strokeWidth={2} dot={{ fill: "var(--primary-red)" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="vd-card p-5" data-testid="dashboard-best-sellers">
          <p className="vd-heading font-semibold mb-3">Best-selling tyres</p>
          {d.best_sellers.length === 0 ? <p className="vd-muted text-sm">No sales yet.</p> : (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={d.best_sellers.map((b) => ({ name: (b.name || "").split(" ")[0], qty: b.qty }))}>
                  <CartesianGrid stroke="var(--border-color)" strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fill: "var(--text-muted)", fontSize: 11 }} />
                  <YAxis tick={{ fill: "var(--text-muted)", fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: "var(--card-bg)", border: "1px solid var(--border-color)", color: "var(--text-heading)" }} />
                  <Bar dataKey="qty" fill="var(--primary-red)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="vd-card p-5" data-testid="dashboard-low-stock-list">
        <p className="vd-heading font-semibold mb-3">Low-stock alerts (&lt; 5 units)</p>
        {d.low_stock.length === 0 ? (
          <p className="vd-muted text-sm">All products are well stocked.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left vd-muted uppercase text-xs tracking-wider">
              <th className="py-2">Product</th><th>Size</th><th>Stock</th><th></th>
            </tr></thead>
            <tbody>
              {d.low_stock.map((p) => (
                <tr key={p.id} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`low-stock-${p.id}`}>
                  <td className="py-3 vd-heading font-medium">{p.brand} {p.model}</td>
                  <td className="vd-muted">{p.size}</td>
                  <td className="font-semibold" style={{ color: "var(--primary-red)" }}>{p.stock_qty}</td>
                  <td><Link to={`/admin/stock/${p.id}`} className="text-sm font-semibold" style={{ color: "var(--primary-red)" }}>Adjust →</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
